import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useHideLovableBadge } from "@/hooks/useHideLovableBadge";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

// Only the landing page is eagerly loaded; everything else is lazy
import Index from "./pages/Index";

// Lazy-loaded pages
const Auth = lazy(() => import("./pages/Auth"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const AIToolsPage = lazy(() => import("./pages/AIToolsPage"));
const LearnAIPage = lazy(() => import("./pages/LearnAIPage"));
const BusinessAIPage = lazy(() => import("./pages/BusinessAIPage"));
const MoroccoAIPage = lazy(() => import("./pages/MoroccoAIPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CertificateVerify = lazy(() => import("./pages/CertificateVerify"));
const ManagerAuth = lazy(() => import("./pages/ManagerAuth"));
const ManagerPasswordChange = lazy(() => import("./pages/ManagerPasswordChange"));
const AdminModeSelector = lazy(() => import("./pages/admin/AdminModeSelector"));

// Lazy-loaded layouts
const PartnerLayout = lazy(() => import("./components/partner/PartnerLayout"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const ManagerLayout = lazy(() => import("./components/manager/ManagerLayout").then(m => ({ default: m.ManagerLayout })));
const YouthProtectedLayout = lazy(() => import("./components/youth/YouthProtectedLayout"));

// Partner pages
const PartnerHome = lazy(() => import("./pages/partner/PartnerHome"));
const PartnerCompanies = lazy(() => import("./pages/partner/PartnerCompanies"));
const PartnerRequests = lazy(() => import("./pages/partner/PartnerRequests"));
const PartnerRequestDetails = lazy(() => import("./pages/partner/PartnerRequestDetails"));
const PartnerDocuments = lazy(() => import("./pages/partner/PartnerDocuments"));
const PartnerDiagnostic = lazy(() => import("./pages/partner/PartnerDiagnostic"));
const PartnerServices = lazy(() => import("./pages/partner/PartnerServices"));
const PartnerEarnings = lazy(() => import("./pages/partner/PartnerEarnings"));
const PartnerSettings = lazy(() => import("./pages/partner/PartnerSettings"));

// Manager section pages
const ManagerDashboardHome = lazy(() => import("./pages/manager/ManagerDashboardHome"));
// ManagerSectionPage has no default export, individual pages are lazy-loaded below

// Admin pages
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPendingApprovals = lazy(() => import("./pages/admin/AdminPendingApprovals"));
const AdminClassroom = lazy(() => import("./pages/admin/training/AdminClassroom"));
const AdminLearningPaths = lazy(() => import("./pages/admin/training/AdminLearningPaths"));
const AdminMeets = lazy(() => import("./pages/admin/training/AdminMeets"));
const AdminCertificates = lazy(() => import("./pages/admin/training/AdminCertificates"));
const AdminTasks = lazy(() => import("./pages/admin/training/AdminTasks"));
const AdminProjects = lazy(() => import("./pages/admin/training/AdminProjects"));
const AdminOpportunities = lazy(() => import("./pages/admin/training/AdminOpportunities"));
const AdminResources = lazy(() => import("./pages/admin/training/AdminResources"));
const AdminAnnouncements = lazy(() => import("./pages/admin/training/AdminAnnouncements"));
const AdminPracticePosts = lazy(() => import("./pages/admin/training/AdminPracticePosts"));
const AdminSkillsPage = lazy(() => import("./pages/admin/AdminSkillsPage"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews"));
const NewsListPage = lazy(() => import("./pages/NewsListPage"));
const NewsArticlePage = lazy(() => import("./pages/NewsArticlePage"));
const AdminYouthProgress = lazy(() => import("./pages/admin/analytics/AdminYouthProgress"));
const AdminVideoAnalytics = lazy(() => import("./pages/admin/analytics/AdminVideoAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminMediaLibrary = lazy(() => import("./pages/admin/AdminMediaLibrary"));
const AdminPlatformManagers = lazy(() => import("./pages/admin/AdminPlatformManagers"));
const AdminServiceRequests = lazy(() => import("./pages/admin/partners/AdminServiceRequests"));
const AdminCommissions = lazy(() => import("./pages/admin/partners/AdminCommissions"));
const AdminPartnersOverview = lazy(() => import("./pages/admin/partners/AdminPartnersOverview"));
const AdminServiceOptions = lazy(() => import("./pages/admin/partners/AdminServiceOptions"));
const AdminServicesManager = lazy(() => import("./pages/admin/partners/AdminServicesManager"));
const PromptsPage = lazy(() => import("./pages/PromptsPage"));
const PromptDetailPage = lazy(() => import("./pages/PromptDetailPage"));
const AdminPrompts = lazy(() => import("./pages/admin/AdminPrompts"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminIdentity = lazy(() => import("./pages/admin/AdminIdentity"));
const AdminIdentityEditor = lazy(() => import("./pages/admin/AdminIdentityEditor"));
const MemberProfilePage = lazy(() => import("./pages/identity/MemberProfilePage"));
const MemberByIdPage = lazy(() => import("./pages/identity/MemberByIdPage"));
const BusinessCardPage = lazy(() => import("./pages/identity/BusinessCardPage"));
const QrRedirectPage = lazy(() => import("./pages/identity/QrRedirectPage"));

// Youth pages
const YouthClassroom = lazy(() => import("./pages/youth/YouthClassroom"));
const YouthQuiz = lazy(() => import("./pages/youth/YouthQuiz"));
const YouthMeet = lazy(() => import("./pages/youth/YouthMeet"));
const YouthProgress = lazy(() => import("./pages/youth/YouthProgress"));
const YouthCertificates = lazy(() => import("./pages/youth/YouthCertificates"));
const YouthOpportunities = lazy(() => import("./pages/youth/YouthOpportunities"));
const YouthResources = lazy(() => import("./pages/youth/YouthResources"));
const YouthLessons = lazy(() => import("./pages/youth/YouthLessons"));
const YouthLearningPaths = lazy(() => import("./pages/youth/YouthLearningPaths"));
const YouthTasks = lazy(() => import("./pages/youth/YouthTasks"));
const YouthProjects = lazy(() => import("./pages/youth/YouthProjects"));
const YouthProjectDetails = lazy(() => import("./pages/youth/YouthProjectDetails"));
const YouthSkills = lazy(() => import("./pages/youth/YouthSkills"));
const YouthDashboardHome = lazy(() => import("./pages/youth/YouthDashboardHome"));
const YouthPracticePosts = lazy(() => import("./pages/youth/YouthPracticePosts"));
const YouthChat = lazy(() => import("./pages/youth/YouthChat"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  useHideLovableBadge();
  return null;
};

// Lazy-loaded manager section components need to be resolved from named exports
const ManagerClassroomPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerClassroomPage })));
const ManagerLearningPathsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerLearningPathsPage })));
const ManagerMeetsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerMeetsPage })));
const ManagerCertificatesPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerCertificatesPage })));
const ManagerTasksPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerTasksPage })));
const ManagerProjectsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerProjectsPage })));
const ManagerOpportunitiesPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerOpportunitiesPage })));
const ManagerResourcesPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerResourcesPage })));
const ManagerPracticePostsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerPracticePostsPage })));
const ManagerAnnouncementsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerAnnouncementsPage })));
const ManagerSkillsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerSkillsPage })));
const ManagerYouthProgressPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerYouthProgressPage })));
const ManagerVideoAnalyticsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerVideoAnalyticsPage })));
const ManagerUsersPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerUsersPage })));
const ManagerApprovalsPage = lazy(() => import("./pages/manager/ManagerSectionPage").then(m => ({ default: m.ManagerApprovalsPage })));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <AppContent />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/tools" element={<AIToolsPage />} />
            <Route path="/learn" element={<LearnAIPage />} />
            <Route path="/business" element={<BusinessAIPage />} />
            <Route path="/morocco-ai" element={<MoroccoAIPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/news" element={<NewsListPage />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
            <Route path="/member/:slug" element={<MemberProfilePage />} />
            <Route path="/member/:slug/card" element={<BusinessCardPage />} />
            <Route path="/profile/:memberId" element={<MemberByIdPage />} />
            <Route path="/qr/:token" element={<QrRedirectPage />} />
            <Route path="/prompts" element={<PromptsPage />} />
            <Route path="/prompts/:slug" element={<PromptDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/admin-setup" element={<AdminSetup />} />
            <Route path="/verify/:code" element={<CertificateVerify />} />
            <Route path="/manager-login" element={<ManagerAuth />} />
            <Route path="/manager-password-change" element={<ManagerPasswordChange />} />
            <Route path="/admin-select" element={<ProtectedRoute allowedRoles={["admin"]}><AdminModeSelector /></ProtectedRoute>} />
            
            {/* Manager Dashboard with nested routes */}
            <Route path="/manager" element={<ProtectedRoute allowedRoles={["platform_manager", "admin"]}><ManagerLayout /></ProtectedRoute>}>
              <Route index element={<ManagerDashboardHome />} />
              <Route path="classroom" element={<ManagerClassroomPage />} />
              <Route path="learning-paths" element={<ManagerLearningPathsPage />} />
              <Route path="meets" element={<ManagerMeetsPage />} />
              <Route path="certificates" element={<ManagerCertificatesPage />} />
              <Route path="tasks" element={<ManagerTasksPage />} />
              <Route path="projects" element={<ManagerProjectsPage />} />
              <Route path="opportunities" element={<ManagerOpportunitiesPage />} />
              <Route path="resources" element={<ManagerResourcesPage />} />
              <Route path="practice-posts" element={<ManagerPracticePostsPage />} />
              <Route path="announcements" element={<ManagerAnnouncementsPage />} />
              <Route path="skills" element={<ManagerSkillsPage />} />
              <Route path="youth-progress" element={<ManagerYouthProgressPage />} />
              <Route path="video-analytics" element={<ManagerVideoAnalyticsPage />} />
              <Route path="users" element={<ManagerUsersPage />} />
              <Route path="approvals" element={<ManagerApprovalsPage />} />
            </Route>
            
            {/* Partner Dashboard with nested routes */}
            <Route path="/partner" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerLayout /></ProtectedRoute>}>
              <Route index element={<PartnerHome />} />
              <Route path="companies" element={<PartnerCompanies />} />
              <Route path="requests" element={<PartnerRequests />} />
              <Route path="requests/:requestId" element={<PartnerRequestDetails />} />
              <Route path="documents" element={<PartnerDocuments />} />
              <Route path="diagnostic" element={<PartnerDiagnostic />} />
              <Route path="services" element={<PartnerServices />} />
              <Route path="earnings" element={<PartnerEarnings />} />
              <Route path="settings" element={<PartnerSettings />} />
            </Route>
            
            {/* Admin Dashboard with nested routes */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/pending" element={<AdminPendingApprovals />} />
              <Route path="training/classroom" element={<AdminClassroom />} />
              <Route path="training/paths" element={<AdminLearningPaths />} />
              <Route path="training/meets" element={<AdminMeets />} />
              <Route path="training/certificates" element={<AdminCertificates />} />
              <Route path="training/tasks" element={<AdminTasks />} />
              <Route path="training/projects" element={<AdminProjects />} />
              <Route path="training/opportunities" element={<AdminOpportunities />} />
              <Route path="training/resources" element={<AdminResources />} />
              <Route path="training/practice-posts" element={<AdminPracticePosts />} />
              <Route path="training/announcements" element={<AdminAnnouncements />} />
              <Route path="skills" element={<AdminSkillsPage />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="prompts" element={<AdminPrompts />} />
              <Route path="analytics/progress" element={<AdminYouthProgress />} />
              <Route path="analytics/videos" element={<AdminVideoAnalytics />} />
              <Route path="partners" element={<AdminPartnersOverview />} />
              <Route path="partners/services" element={<AdminServicesManager />} />
              <Route path="partners/options" element={<AdminServiceOptions />} />
              <Route path="partners/requests" element={<AdminServiceRequests />} />
              <Route path="partners/commissions" element={<AdminCommissions />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="media" element={<AdminMediaLibrary />} />
              <Route path="managers" element={<AdminPlatformManagers />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="identity" element={<AdminIdentity />} />
              <Route path="identity/new" element={<AdminIdentityEditor />} />
              <Route path="identity/:id" element={<AdminIdentityEditor />} />
            </Route>
            
            {/* Youth Dashboard with nested routes */}
            <Route path="/youth" element={<ProtectedRoute allowedRoles={["youth"]}><YouthProtectedLayout /></ProtectedRoute>}>
              <Route index element={<YouthClassroom />} />
              <Route path="home" element={<YouthDashboardHome />} />
              <Route path="quiz" element={<YouthQuiz />} />
              <Route path="meet" element={<YouthMeet />} />
              <Route path="progress" element={<YouthProgress />} />
              <Route path="certificates" element={<YouthCertificates />} />
              <Route path="opportunities" element={<YouthOpportunities />} />
              <Route path="resources" element={<YouthResources />} />
              <Route path="lessons" element={<YouthLessons />} />
              <Route path="paths" element={<YouthLearningPaths />} />
              <Route path="tasks" element={<YouthTasks />} />
              <Route path="projects" element={<YouthProjects />} />
              <Route path="projects/:projectId" element={<YouthProjectDetails />} />
              <Route path="skills" element={<YouthSkills />} />
              <Route path="practice-posts" element={<YouthPracticePosts />} />
              <Route path="chat" element={<YouthChat />} />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </LanguageProvider>
  </QueryClientProvider>
);

export default App;
